import {announce} from '@react-aria/live-announcer'

import MicrophoneSvg from '@/assets/icons/microphone.svg?react'
import MuteSvg from '@/assets/icons/mute.svg?react'
import send from '@/assets/icons/send.svg'
import stopSvg from '@/assets/icons/stop.svg'
import AgreementText from '@/components/AgreementText/AgreementText'
import AutoGrowTextarea from '@/components/AutoGrow/AutoGrowTextarea'
import Messages from '@/components/ChatSection/componnents/Messages/Messages'
import {
  messageInputAtom,
  messageInputIsDisabledAtom,
  messageInputPlaceholderAtom,
} from '@/components/ChatSection/states'
import useVoiceToText from '@/hooks/useVoiceToText'
import type {Thread} from '@/models/threadModel'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './ChatSection.module.scss'
export interface ChatSectionControl {
  focusToMessageInput: MemoizedCallback<() => void>
  scrollToBottomChat: MemoizedCallback<() => void>
}

interface Props {
  ref: Ref<ChatSectionControl>
  onActiveTheadIdChange?: MemoizedCallback<(id: string | undefined) => void>
  // onQChange?: MemoizedCallback<(q: string) => void>
  submitMessage: MemoizedCallback<(e: string | React.FormEvent, isRegenerate?: boolean) => void>
  isGenerating: boolean
  stopGenerate: MemoizedCallback<(except?: symbol | SyntheticEvent, isActive?: boolean) => void>
  activeThreadId: Thread['id'] | undefined
}

export default deepMemo(function ChatSection({
  ref,
  submitMessage,
  isGenerating,
  stopGenerate,
  activeThreadId,
  children,
}: PropsWithChildren<Props>) {
  const messageInputRef = useRef<HTMLTextAreaElement>(null)
  const chatContentRef = useRef<HTMLDivElement>(null)

  const [q, setQ] = useAtom(messageInputAtom)
  const qIsEmpty = !q

  const [sendMessagePlaceholder, setSendMessagePlaceholder] = useAtom(messageInputPlaceholderAtom)

  const messageInputIsDisabled = useAtomValue(messageInputIsDisabledAtom)

  const scrollToBottomChat = useCallback(() => {
    // Wait for all messages to be rendered
    setTimeout(() => {
      if (chatContentRef.current) {
        chatContentRef.current.scrollTo({
          top: chatContentRef.current.scrollHeight,
          behavior: 'smooth',
        })
      }
    }, 100)
  }, [])

  const focusToMessageInput = useCallback(() => {
    if (messageInputRef.current) {
      messageInputRef.current.focus()
      // Set the cursor to the end of the input
      messageInputRef.current.setSelectionRange(
        messageInputRef.current.value.length,
        messageInputRef.current.value.length,
      )
    }
  }, [])

  const onQChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
    e => {
      setQ(e.currentTarget.value)
    },
    [setQ],
  )

  useImperativeHandle(ref, () => {
    return {
      scrollToBottomChat,
      focusToMessageInput,
    } satisfies ChatSectionControl
  }, [focusToMessageInput, scrollToBottomChat])

  const {startListening, stopListening, transcript, reset, isSpeechRecognitionAvailable} =
    useVoiceToText()
  const latestSpeechRecognitionIsAvailable = useLatest(isSpeechRecognitionAvailable)
  const [isListening, setIsListening] = useState(false)
  const latestStartListening = useLatest(startListening)
  const latestIsListening = useLatest(isListening)
  const latestStopListening = useLatest(stopListening)
  const latestReset = useLatest(reset)

  const onVoiceModePressed = useCallback<MouseEventHandler<HTMLButtonElement>>(e => {
    e.preventDefault()
    if (!latestSpeechRecognitionIsAvailable.current) return
    if (latestIsListening.current) {
      latestOnVoiceModeEnd.current()
    } else {
      latestStartListening.current()
      setIsListening(true)
      announce('Listening...', 'polite')
    }
  }, [])

  const onVoiceModeEnd = useCallback(() => {
    if (!latestIsListening.current) return
    if (!latestSpeechRecognitionIsAvailable.current) return
    latestStopListening.current()
    latestReset.current()
    setIsListening(false)
    messageInputRef.current?.focus()
  }, [])
  const latestOnVoiceModeEnd = useLatest(onVoiceModeEnd)

  useEffect(() => {
    if (!transcript) return
    setQ(transcript)
  }, [transcript])

  return (
    <div className={style.ChatSection}>
      <main
        ref={chatContentRef}
        className={style.ChatContent}
        aria-label='Conversation content'
        {...skipTargetProps('conversation-content')}
      >
        <VisuallyHidden as='h2'>Conversation content</VisuallyHidden>
        <Messages
          activeThreadId={activeThreadId}
          isGenerating={isGenerating}
          submitMessage={submitMessage}
          setQ={setQ}
          setSendMessagePlaceholder={setSendMessagePlaceholder}
        >
          {children}
        </Messages>
      </main>
      <form
        onSubmit={submitMessage}
        className={style.MessageBox}
        aria-label='Send message'
        {...skipTargetProps('message-form')}
      >
        <div className={style.MessageInputWrapper}>
          <AutoGrowTextarea
            aria-label='Your message'
            ref={messageInputRef}
            className={style.MessageInput}
            wrapperClassName={style.MessageInputWrapper}
            placeholder={sendMessagePlaceholder}
            value={q}
            onChange={onQChange}
            rows={1}
            enterToSubmit
            disabled={messageInputIsDisabled}
          />
          {isGenerating ? (
            <button
              aria-label='Stop generating'
              onClick={stopGenerate}
              className={style.StopGeneratingBtn}
            >
              <img src={stopSvg} alt='' />
            </button>
          ) : (
            <>
              {isSpeechRecognitionAvailable && (
                <button
                  type='button'
                  onClick={onVoiceModePressed}
                  className={clsx(style.DictationModeBtn)}
                  aria-label={isListening ? 'Stop dictation mode' : 'Start dictation mode'}
                  onBlur={onVoiceModeEnd}
                >
                  {isListening ? <MuteSvg /> : <MicrophoneSvg />}
                </button>
              )}
              <button
                type='submit'
                className={clsx(style.MessageSubmit, !qIsEmpty && style.Active)}
                disabled={qIsEmpty}
                aria-label='Send message'
              >
                <img src={send} alt='' />
              </button>
            </>
          )}
        </div>
        <AgreementText noPadding className={style.MessageBoxAgreementText} />
      </form>
    </div>
  )
})
